/**
 * Vendor Hooks
 * ============
 * React Query hooks for motorcycle parts vendors.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { vendorApi } from '@/services/api'
import type { Vendor, VendorCreate, VendorUpdate } from '@/types'

// =============================================================================
// Query Keys
// =============================================================================

export const vendorKeys = {
  all: ['vendors'] as const,
  lists: () => [...vendorKeys.all, 'list'] as const,
  list: () => [...vendorKeys.lists()] as const,
  details: () => [...vendorKeys.all, 'detail'] as const,
  detail: (id: number) => [...vendorKeys.details(), id] as const,
}

// =============================================================================
// Vendor Hooks
// =============================================================================

/**
 * Fetch all vendors
 */
export function useVendors() {
  return useQuery({
    queryKey: vendorKeys.list(),
    queryFn: async () => {
      const response = await vendorApi.list()
      return response.data as Vendor[]
    },
  })
}

/**
 * Fetch a single vendor by ID
 */
export function useVendor(id: number) {
  return useQuery({
    queryKey: vendorKeys.detail(id),
    queryFn: async () => {
      const response = await vendorApi.get(id)
      return response.data as Vendor
    },
    enabled: !!id,
  })
}

/**
 * Create a new vendor
 */
export function useCreateVendor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: VendorCreate) => vendorApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
    },
  })
}

/**
 * Update an existing vendor
 */
export function useUpdateVendor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: VendorUpdate }) =>
      vendorApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(id) })
    },
  })
}

/**
 * Delete a vendor
 */
export function useDeleteVendor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => vendorApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
    },
  })
}
